import * as React from 'react';

import { IStoreDiagnosticData } from 'features/store/components/store-diagnostic-modal';
import { useStoreCard } from 'features/store/hooks';
import { useStoreAvailabilityFns } from 'features/store/hooks/use-store-availability-fns';
import { getStoreLocalTime } from 'features/store/utils';

import { IRestaurant } from '@rbi-ctg/store';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

interface IUseDiagnosticsOptions {
  restaurant: IRestaurant;
}

export const useDiagnostics = ({ restaurant }: IUseDiagnosticsOptions) => {
  const isEnabled = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const [showModal, setShowModal] = React.useState(false);

  const onClick = React.useMemo(() => {
    return isEnabled ? () => setShowModal(true) : undefined;
  }, [isEnabled]);

  const onDismiss = React.useCallback(() => setShowModal(false), []);

  const {
    driveThruOpen,
    diningRoomOpen,
    isMobileOrderingAvailable,
    mobileOrderDriveThruOpen,
    isRestaurantPosOnline,
  } = useStoreCard({ restaurant, shouldGetRemoteAvailabilityData: false });

  const { isStoreAvailableForPickup } = useStoreAvailabilityFns();
  const enableOrdering = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);

  const data: IStoreDiagnosticData = {
    enableOrdering,
    driveThruOpen,
    diningRoomOpen,
    isRestaurantOpen: isStoreAvailableForPickup(restaurant, getStoreLocalTime()),
    isMobileOrderingAvailable,
    isRestaurantPosOnline,
    mobileOrderDriveThruOpen,
    restaurantName: restaurant.name || '',
    restaurantHasMobileOrdering: Boolean(restaurant.hasMobileOrdering),
    restaurantCountry: restaurant?.physicalAddress?.country || '',
    restaurantStreetAddress: restaurant?.physicalAddress?.address1 || '',
  };

  return {
    data,
    onClick,
    onDismiss,
    showModal,
  };
};
